import {
  getClubCategoryRanking,
  getPlayerCategoryRankingEntries,
} from '@/api/category-ranking/categoryRankingApi';
import {
  CATEGORY_RANKING_PERIOD,
  CATEGORY_RANKING_PERIOD_OPTIONS,
  RANKING_POINTS_SOURCE,
  type CategoryRankingEntry,
  type CategoryRankingEntryDetail,
  type CategoryRankingPeriod,
} from '@/api/category-ranking/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useEloCategoriesState } from '@/hooks/useEloCategories';
import { useSportsStore } from '@/store/useSportsStore';
import { getCategoryRankingStageLabel } from '@/utils/category-ranking-utils';
import { normalizeSportUid } from '@/utils/club-utils';
import { handleGlobalError } from '@/utils/error-handler';
import { formatDateForUser } from '@/utils/formatters';
import { getGenderLabel } from '@/utils/gender-utils';
import { ChevronDown, LoaderCircle, Trophy, Upload } from 'lucide-react';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CategoryRankingPointsInfoModal } from './CategoryRankingPointsInfoModal';
import { RankingImportBatchesModal } from './RankingImportBatchesModal';
import { RankingImportModal } from './RankingImportModal';

const ALL_GENDERS = 'ALL';
const BREAKDOWN_PAGE_SIZE = 5;
const RANKING_PAGE_SIZE = 25;

/** Player genders the ranking can be filtered by — matches the backend `GenderEnum`. */
const GENDERS: { label: string; value: string }[] = [
  { label: 'Todos', value: ALL_GENDERS },
  ...['MALE', 'FEMALE', 'OTHER'].map((value) => ({
    label: getGenderLabel(value),
    value,
  })),
];

interface CategoryRankingPanelProps {
  /** Club whose ranking is shown. When absent, the panel renders its filters disabled. */
  clubId?: string;
  /**
   * Sports the club has courts for — the only ones it can be ranked by. Callers already
   * fetch the club, so they derive these with `getClubSportUids` instead of the panel
   * asking for it a second time. `undefined` means they are still loading; `null` that
   * they could not be determined, and the whole catalog is offered as a fallback.
   *
   * Required on purpose: omitting it would leave the panel waiting for sports forever.
   */
  clubSportUids: string[] | null | undefined;
}

export const CategoryRankingPanel: React.FC<CategoryRankingPanelProps> = ({
  clubId,
  clubSportUids,
}) => {
  const {
    sportsData,
    fetchSports,
    isInitialized,
    isLoading: sportsLoading,
    error: sportsError,
  } = useSportsStore();
  const clubSportsPending = clubSportUids === undefined;
  const sports = useMemo(() => {
    const allSports = sportsData.map((sportData) => sportData.sport);
    // Offering the whole catalog while the club's courts are in flight would pick a
    // sport the club may not even have, and fetch its categories for nothing.
    if (clubSportsPending) return [];
    if (!clubSportUids) return allSports;
    const offered = new Set(clubSportUids);
    return allSports.filter((sport) =>
      offered.has(normalizeSportUid(sport.uid)),
    );
  }, [sportsData, clubSportUids, clubSportsPending]);

  const [selectedSport, setSelectedSport] = useState<string | undefined>(
    undefined,
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<
    number | undefined
  >(undefined);
  const [gender, setGender] = useState(ALL_GENDERS);
  const [period, setPeriod] = useState<CategoryRankingPeriod>(
    CATEGORY_RANKING_PERIOD.ALL_TIME,
  );
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [ranking, setRanking] = useState<{
    entries: CategoryRankingEntry[];
    total: number;
    page: number;
  }>({ entries: [], total: 0, page: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());
  const [breakdowns, setBreakdowns] = useState<
    Record<string, { entries: CategoryRankingEntryDetail[]; total: number }>
  >({});
  const [loadingEmails, setLoadingEmails] = useState<Set<string>>(new Set());
  // Bumped whenever the filters change, so an in-flight "Ver más" fetch started under the
  // previous filters can detect it was superseded and discard its response instead of
  // appending stale rows.
  const filtersGenerationRef = useRef(0);

  const { hasOwnerRole, hasManagerRole, hasAdminRole } = useAuth();
  const [importOpen, setImportOpen] = useState(false);
  const [batchesOpen, setBatchesOpen] = useState(false);
  // Bumped after an import or a batch delete. Refetches the ranking and, through the second effect,
  // discards any open breakdown so it is re-fetched on the next expand — never shown stale.
  const [reloadKey, setReloadKey] = useState(0);

  const canImport = Boolean(clubId) && (hasOwnerRole || hasManagerRole || hasAdminRole);

  // Both selects fall back to the first available option until the user picks one,
  // and re-fall-back when the current selection is not valid for the loaded catalog.
  const sport =
    selectedSport !== undefined && sports.some((s) => s.uid === selectedSport)
      ? selectedSport
      : sports[0]?.uid;

  const { categories, loading: categoriesLoading } =
    useEloCategoriesState(sport);

  const categoryId =
    selectedCategoryId !== undefined &&
    categories.some((c) => c.id === selectedCategoryId)
      ? selectedCategoryId
      : categories[0]?.id;

  // Sports and categories both feed the filters: until they settle there is nothing to
  // query, and a failure on either leaves them empty — which must not read as "the club
  // has no ranking".
  const catalogLoading =
    (!isInitialized && !sportsError) ||
    sportsLoading ||
    clubSportsPending ||
    categoriesLoading;

  useEffect(() => {
    if (!isInitialized) fetchSports();
  }, [isInitialized, fetchSports]);

  useEffect(() => {
    // Nothing to query yet (or ever, if the catalog failed to load): the render below
    // tells those cases apart, but the spinner must not stay up forever.
    if (!clubId || sport === undefined || categoryId === undefined) {
      setRanking({ entries: [], total: 0, page: 0 });
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getClubCategoryRanking(clubId, {
      categoryId,
      gender: gender === ALL_GENDERS ? undefined : gender,
      page: 0,
      period,
      query: debouncedSearch || undefined,
      size: RANKING_PAGE_SIZE,
      sport,
    })
      .then(({ entries, total }) => {
        if (!cancelled) setRanking({ entries, total, page: 0 });
      })
      .catch((error) => {
        if (cancelled) return;
        setRanking({ entries: [], total: 0, page: 0 });
        handleGlobalError(
          error,
          'Error',
          'No se pudo cargar el ranking del club.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clubId, categoryId, gender, period, debouncedSearch, sport, reloadKey]);

  // The breakdown depends on the filters: when any changes, discard it instead of
  // re-fetching for rows the user may no longer have open.
  useEffect(() => {
    filtersGenerationRef.current += 1;
    setExpandedEmails(new Set());
    setBreakdowns({});
    setLoadingEmails(new Set());
    setLoadingMore(false);
  }, [clubId, categoryId, gender, period, debouncedSearch, sport, reloadKey]);

  const loadMoreRanking = () => {
    if (!clubId || sport === undefined || categoryId === undefined) return;
    const generation = filtersGenerationRef.current;
    const nextPage = ranking.page + 1;
    setLoadingMore(true);
    getClubCategoryRanking(clubId, {
      categoryId,
      gender: gender === ALL_GENDERS ? undefined : gender,
      page: nextPage,
      period,
      query: debouncedSearch || undefined,
      size: RANKING_PAGE_SIZE,
      sport,
    })
      .then(({ entries, total }) => {
        if (generation !== filtersGenerationRef.current) return;
        setRanking((current) => ({
          entries: [...current.entries, ...entries],
          total,
          page: nextPage,
        }));
      })
      .catch((error) => {
        if (generation !== filtersGenerationRef.current) return;
        handleGlobalError(
          error,
          'Error',
          'No se pudo cargar el ranking del club.',
        );
      })
      .finally(() => {
        if (generation !== filtersGenerationRef.current) return;
        setLoadingMore(false);
      });
  };

  const fetchBreakdownPage = (email: string, page: number) => {
    if (!clubId || sport === undefined || categoryId === undefined) return;
    const generation = filtersGenerationRef.current;
    setLoadingEmails((current) => new Set(current).add(email));
    getPlayerCategoryRankingEntries(clubId, {
      categoryId,
      email,
      page,
      period,
      size: BREAKDOWN_PAGE_SIZE,
      sport,
    })
      .then(({ entries: pageEntries, total }) => {
        if (generation !== filtersGenerationRef.current) return;
        setBreakdowns((current) => ({
          ...current,
          [email]: {
            entries: [...(current[email]?.entries ?? []), ...pageEntries],
            total,
          },
        }));
      })
      .catch((error) => {
        if (generation !== filtersGenerationRef.current) return;
        handleGlobalError(
          error,
          'Error',
          'No se pudo cargar el detalle del jugador.',
        );
      })
      .finally(() => {
        if (generation !== filtersGenerationRef.current) return;
        setLoadingEmails((current) => {
          const next = new Set(current);
          next.delete(email);
          return next;
        });
      });
  };

  const toggleExpanded = (email: string) => {
    // Computing `willExpand` and calling the fetch outside the updater keeps the
    // updater pure — React 18 StrictMode can invoke it twice, which would otherwise
    // risk a duplicate request.
    const willExpand = !expandedEmails.has(email);
    setExpandedEmails((current) => {
      const next = new Set(current);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
    if (willExpand && !breakdowns[email]) fetchBreakdownPage(email, 0);
  };

  return (
    <>
      <div className="mb-6 flex flex-wrap gap-3">
        <Select value={sport ?? ''} onValueChange={setSelectedSport}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Deporte" />
          </SelectTrigger>
          <SelectContent>
            {sports.map((s) => (
              <SelectItem key={s.uid} value={s.uid}>
                {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={categoryId !== undefined ? String(categoryId) : ''}
          onValueChange={(value) => setSelectedCategoryId(Number(value))}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Categoría" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((c) => (
              <SelectItem key={c.id} value={String(c.id)}>
                {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={gender} onValueChange={setGender}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Género" />
          </SelectTrigger>
          <SelectContent>
            {GENDERS.map((g) => (
              <SelectItem key={g.value} value={g.value}>
                {g.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={period}
          onValueChange={(value) => setPeriod(value as CategoryRankingPeriod)}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Período" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORY_RANKING_PERIOD_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Input
          className="w-[220px]"
          placeholder="Buscar jugador"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <CategoryRankingPointsInfoModal />

        {canImport && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBatchesOpen(true)}
            >
              Importaciones
            </Button>
            <Button type="button" onClick={() => setImportOpen(true)}>
              <Upload size={16} className="mr-2" />
              Importar puntos
            </Button>
          </>
        )}
      </div>

      {!clubId ? (
        <div className="flex flex-col items-center gap-2 py-16 text-gray-500">
          <Trophy className="h-8 w-8" />
          <p className="text-sm">Elegí un club para ver su ranking.</p>
        </div>
      ) : catalogLoading || loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <LoaderCircle className="mx-auto h-8 w-8 animate-spin text-blue-600" />
            <p className="mt-2 text-sm text-gray-600">Cargando ranking...</p>
          </div>
        </div>
      ) : sport === undefined || categoryId === undefined ? (
        <div className="flex flex-col items-center gap-2 py-16 text-gray-500">
          <Trophy className="h-8 w-8" />
          <p className="text-sm">
            {sport === undefined
              ? sportsData.length > 0
                ? 'Este club todavía no tiene canchas cargadas, así que no hay deportes para rankear.'
                : 'No se pudieron cargar los deportes. Recargá la página para reintentar.'
              : 'No se pudieron cargar las categorías de este deporte.'}
          </p>
        </div>
      ) : ranking.entries.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-gray-500">
          <Trophy className="h-8 w-8" />
          <p className="text-sm">
            {debouncedSearch
              ? 'No se encontraron jugadores que coincidan con la búsqueda.'
              : 'Todavía no hay puntos en esta categoría para este club.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {ranking.entries.map((entry, index) => {
            const isExpanded = expandedEmails.has(entry.email);
            const breakdown = breakdowns[entry.email];
            const isLoadingBreakdown = loadingEmails.has(entry.email);

            return (
              <div
                key={entry.email}
                className={index > 0 ? 'border-t border-gray-200' : ''}
              >
                <button
                  type="button"
                  onClick={() => toggleExpanded(entry.email)}
                  aria-expanded={isExpanded}
                  className="flex w-full items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                >
                  <span className="w-8 shrink-0 text-center text-sm font-bold text-gray-500">
                    {entry.position}
                  </span>
                  <span className="flex-1 truncate text-sm font-medium">
                    {entry.playerName}
                  </span>
                  <span className="text-lg font-bold">{entry.points}</span>
                  <span className="text-xs text-gray-500">pts</span>
                  <ChevronDown
                    size={16}
                    className={`shrink-0 text-gray-400 transition-transform ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 sm:pl-16">
                    {isLoadingBreakdown && !breakdown ? (
                      <div className="flex justify-center py-4">
                        <LoaderCircle className="h-5 w-5 animate-spin text-blue-600" />
                      </div>
                    ) : !breakdown || breakdown.entries.length === 0 ? (
                      <p className="py-2 text-sm text-gray-500">
                        No hay torneos que hayan sumado puntos con estos filtros.
                      </p>
                    ) : (
                      <>
                        <ul className="space-y-2">
                          {/*
                            Keyed by index: an imported entry has no tournamentId, and its
                            business fields do not identify it either — points derive from the
                            stage and earnedAt is the batch's date, so a player who placed the
                            same twice in one batch yields two identical entries. The list is
                            append-only ("Ver más" concatenates pages) and never reordered or
                            filtered, so the index is stable for each entry.
                          */}
                          {breakdown.entries.map((detail, detailIndex) => (
                            <li
                              key={detailIndex}
                              className="flex items-start justify-between gap-3"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm font-medium text-gray-800">
                                  {detail.tournamentName ?? 'Puntos migrados'}
                                  {detail.source ===
                                    RANKING_POINTS_SOURCE.IMPORTED && (
                                    <Badge
                                      variant="outline"
                                      className="ml-2 border-amber-300 bg-amber-50 text-amber-700"
                                    >
                                      Migrado
                                    </Badge>
                                  )}
                                </p>
                                <p className="text-xs text-gray-500">
                                  {getCategoryRankingStageLabel(detail.stage)} ·{' '}
                                  {formatDateForUser(detail.earnedAt)}
                                </p>
                              </div>
                              <span className="shrink-0 text-sm font-bold text-gray-700">
                                {detail.points} pts
                              </span>
                            </li>
                          ))}
                        </ul>

                        {breakdown.total > breakdown.entries.length && (
                          <div className="mt-3 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={() =>
                                fetchBreakdownPage(
                                  entry.email,
                                  breakdown.entries.length / BREAKDOWN_PAGE_SIZE,
                                )
                              }
                              disabled={isLoadingBreakdown}
                              className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
                            >
                              {isLoadingBreakdown ? 'Cargando...' : 'Ver más'}
                            </button>
                            <span className="text-xs text-gray-500">
                              Mostrando {breakdown.entries.length} de{' '}
                              {breakdown.total}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {ranking.total > ranking.entries.length && (
            <div className="flex items-center gap-3 border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={loadMoreRanking}
                disabled={loadingMore}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {loadingMore ? 'Cargando...' : 'Ver más'}
              </button>
              <span className="text-xs text-gray-500">
                Mostrando {ranking.entries.length} de {ranking.total} jugadores
              </span>
            </div>
          )}
        </div>
      )}

      {canImport && clubId && (
        <>
          <RankingImportModal
            clubId={clubId}
            sports={sports}
            defaultSportUid={sport}
            open={importOpen}
            onOpenChange={setImportOpen}
            onImported={() => setReloadKey((key) => key + 1)}
          />
          <RankingImportBatchesModal
            clubId={clubId}
            sports={sports}
            open={batchesOpen}
            onOpenChange={setBatchesOpen}
            onChanged={() => setReloadKey((key) => key + 1)}
          />
        </>
      )}
    </>
  );
};
